<script setup lang="ts">
/**
 * Chat header: "ให้ AI ดูแล" (docs/ai-mode.md). Disabled with the server's Thai reason when the AI may not take this
 * chat (owner switch off · not a test account · comment/group · closed). While the AI is in charge the button STAYS,
 * pressed and pulsing, as "AI ดูแลอยู่ · กดเพื่อหยุด" (owner 27 ก.ย. 69: "อย่าหายแต่ทำให้รู้ว่ากำลังใช้ AI เผื่ออยากให้
 * หยุดเพื่อตอบเอง") — the strip under the header keeps its own "หยุด AI" too. Owner 28 ก.ย. 23:15: an icon that says
 * both "working" and "press to stop" — a turning ring around a stop square (like "stop generating"); hover reads "หยุด AI".
 */
import { computed } from 'vue'
import { useAiMode } from '../../stores/aimode'
import { useInbox } from '../../stores/inbox'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ convId: string }>()
const inbox = useInbox()
const aimode = useAiMode()

const conv = computed(() => inbox.convs[props.convId] ?? null)
const view = computed(() => aimode.views[props.convId] ?? null)
const on = computed(() => !!conv.value?.ai_mode)
const allowed = computed(() => !!view.value?.can_turn_on.ok)
const busy = computed(() => !!aimode.busy[props.convId])
const tip = computed(() =>
  !view.value
    ? 'กำลังตรวจว่าให้ AI ดูแลแชทนี้ได้ไหม'
    : allowed.value
      ? 'ให้ AI คุยและปิดการขายในแชทนี้ (ทดสอบ) · พนักงานพิมพ์ส่งเมื่อไรก็ได้ AI จะหยุดทันที'
      : (view.value.can_turn_on.reason ?? 'ให้ AI ดูแลแชทนี้ไม่ได้'),
)
</script>

<template>
  <span v-if="on" class="aim" title="AI กำลังดูแลแชทนี้ · กดเพื่อหยุด แล้วตอบเอง">
    <button
      type="button"
      class="btn aim-b aim-on"
      aria-pressed="true"
      aria-label="AI กำลังดูแลแชทนี้ กดเพื่อหยุด AI"
      :disabled="busy"
      @click="aimode.setOn(convId, false)"
    >
      <span class="aim-stop" aria-hidden="true"><i></i></span
      ><span class="aim-l aim-sw"><span class="aim-l1">AI ดูแลอยู่</span><span class="aim-l2" aria-hidden="true">หยุด AI</span></span
      ><span class="aim-s" aria-hidden="true">AI</span>
    </button>
  </span>
  <span v-else class="aim" :title="tip">
    <button
      type="button"
      class="btn aim-b"
      :disabled="!allowed || busy"
      :aria-label="allowed ? 'ให้ AI ดูแลแชทนี้' : `ให้ AI ดูแลไม่ได้: ${tip}`"
      @click="aimode.setOn(convId, true)"
    >
      <Icon name="sparkles" /><span class="aim-l">ให้ AI ดูแล</span><span class="aim-s" aria-hidden="true">AI</span>
    </button>
  </span>
</template>

<style scoped>
.aim {
  display: inline-flex;
}
.aim-b {
  border-color: #b9dccb;
  color: var(--ac2);
  font-weight: 600;
}
.aim-b :deep(.ic) {
  color: var(--ac);
}
.aim-b:hover:not(:disabled) {
  background: var(--acs);
}
/* AI in charge: pressed look (green fill) + a breathing dot, so it reads "on" at a glance; click = stop */
.aim-on {
  background: var(--acs);
  border-color: var(--ac);
  color: var(--ac2);
}
.aim-on:hover:not(:disabled) {
  background: #dff0e7;
}
/* working + stoppable: a ring turning around a stop square; hover stops the ring and fills it */
.aim-stop {
  position: relative;
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 16px;
  height: 16px;
}
.aim-stop::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(14, 122, 92, 0.2);
  border-top-color: var(--ac);
  animation: aim-spin 0.9s linear infinite;
}
.aim-stop i {
  width: 6px;
  height: 6px;
  border-radius: 1.5px;
  background: var(--ac2);
}
.aim-on:hover:not(:disabled) .aim-stop::before {
  animation-play-state: paused;
  border-color: var(--ac2);
}
@keyframes aim-spin {
  to {
    transform: rotate(360deg);
  }
}
/* both labels share one cell, so hover swapping them never moves the header */
.aim-sw {
  display: inline-grid;
}
.aim-sw > span {
  grid-area: 1 / 1;
}
.aim-l2 {
  visibility: hidden;
}
.aim-on:hover:not(:disabled) .aim-l1 {
  visibility: hidden;
}
.aim-on:hover:not(:disabled) .aim-l2 {
  visibility: visible;
}
@media (prefers-reduced-motion: reduce) {
  .aim-stop::before {
    animation: none;
  }
}
/* a disabled button gets no hover: let the wrapper show the reason (title) */
.aim:has(.aim-b:disabled) {
  cursor: not-allowed;
}
.aim-b:disabled {
  pointer-events: none;
  border-color: var(--line);
  color: #7d8c85;
  background: #f7f9f8;
  font-weight: 500;
}
.aim-b:disabled :deep(.ic) {
  color: #9fb3a9;
}
/* the header also holds ย้ายแชท / ปักหมุด / ปิดแชท: the customer's name keeps its room —
   "AI" under 700 px of chat column (1440 wide screens), icon only under 520 px (1280), like "ย้ายแชท" */
.aim-s {
  display: none;
}
@container (max-width: 700px) {
  .aim-l {
    display: none;
  }
  .aim-s {
    display: inline;
  }
}
@container (max-width: 520px) {
  .aim-s {
    display: none;
  }
  .aim-b {
    width: 32px;
    padding: 0;
    justify-content: center;
  }
}
</style>
